import React, { forwardRef, useImperativeHandle, useState, useRef, useMemo } from "react";
import styled from "styled-components";
import BottomModalV2, { RefProps } from "../common/BottomModalV2";
import Box from "../common/Box";
import SearchInput from "../common/SearchInput";
import Button from "../common/Button";
import SVG from "../../utils/SVG";
import FulfillListItem from "./FulfillListItem";
import { useFulfillListQuery } from "../../api/fulfillAPI";
import { useAppSelector } from "../../store/actions/action";
import { useDispatch } from "react-redux";
import { setNoticeRegister } from "../../store/reducers/noticeReduce";

// 이행매장 조회 시 가져올 최대 개수
const EXECUTE_STORES_FETCH_LIMIT = 1000;

interface Store {
  store_id: string;
  store_name?: string;
  name?: string;
  user_id?: string;
  execute_yn?: 'Y' | 'N';
  [key: string]: any;
}

interface IPreviousExecuteStoresModalProps {
  selectedNotice: any;
  onApply: () => void;
}

const PreviousExecuteStoresModal = (props: IPreviousExecuteStoresModalProps, ref: React.Ref<RefProps>) => {
  const modalRef = useRef<RefProps>(null);
  const [searchContent, setSearchContent] = useState("");
  const [showUnfulfilledOnly, setShowUnfulfilledOnly] = useState(false);
  const noticeData = useAppSelector((state) => state.noticeReduce);
  const dispatch = useDispatch();

  // 선택된 공지의 이행매장 리스트 조회
  const { data: executeStoresData, isLoading, refetch, error } = useFulfillListQuery(
    {
      notice_idx: props.selectedNotice?.notice_idx,
      search: {
        limit: EXECUTE_STORES_FETCH_LIMIT  // 이행매장 전체 조회를 위해 limit 설정
      }
    },
    { skip: !props.selectedNotice }
  );


  useImperativeHandle(ref, () => ({
    modalOpen: () => {
      modalRef.current?.modalOpen();
      if (props.selectedNotice) {
        refetch(); // 데이터 새로 가져오기
      }
    },
    modalClose: () => {
      // 모달 닫을 때 tempStores 초기화
      dispatch(setNoticeRegister({ tempStores: [] }));
      modalRef.current?.modalClose();
    },
  }));

  // 데이터 로드 시 tempStores를 기존 beforeStores에 있는 매장들로만 초기화
  React.useEffect(() => {
    if (executeStoresData?.data?.data) {
      const storeData = Array.isArray(executeStoresData.data.data) ? executeStoresData.data.data : [];
      const allStoreIds = storeData.map((store: any) => store.store_id);
      const currentStores = noticeData.beforeStores || [];

      // beforeStores에 있는 매장 중에서 현재 이행매장 리스트에 있는 매장들만 선택
      const existingStores = allStoreIds.filter((storeId: string) => currentStores.includes(storeId));
      dispatch(setNoticeRegister({ tempStores: existingStores }));
    }
  }, [executeStoresData, noticeData.beforeStores, dispatch]);

  const handleClose = () => {
    // 모달 닫을 때 tempStores 초기화
    dispatch(setNoticeRegister({ tempStores: [] }));
    return true; // 닫기 허용
  };

  const searchContents = (e: any) => {
    // 검색 버튼 클릭 시 현재 searchContent 상태 유지
    // SearchInput 컴포넌트의 onChange로 이미 업데이트됨
  };

  const onKeyPress = (e: any) => {
    if (e.key === "Enter") {
      // Enter 키 입력 시 검색 실행
      // searchContent는 onChange로 이미 업데이트됨
    }
  };

  const deleteSearchContents = () => {
    setSearchContent("");
  };

  // 검색된 매장 리스트 필터링 및 데이터 변환 (API 응답을 Store 타입에 맞게 변환)
  // API 응답 구조: {statusCode, message, data: {data: Array, meta, links}}
  const rawStoreData = executeStoresData?.data?.data || [];

  const storeData = useMemo(() => {
    return Array.isArray(rawStoreData) ? rawStoreData.map((store: Store) => ({
      ...store,
      user_id: store.store_id,  // store_id를 user_id로 매핑
      name: store.store_name    // store_name을 name으로 매핑
    })) : [];
  }, [rawStoreData]);

  const searchLower = useMemo(() => {
    return searchContent?.toLowerCase().trim() || '';
  }, [searchContent]);

  // 미이행 필터 적용 - execute_yn 필드 사용
  const statusFilteredStores = useMemo(() => {
    if (showUnfulfilledOnly) {
      return storeData.filter((store: Store) => !store.execute_yn || store.execute_yn === 'N');
    }
    return storeData;
  }, [storeData, showUnfulfilledOnly]);

  // 검색 필터 적용
  const filteredStores = useMemo(() => {
    if (!searchLower) return statusFilteredStores;

    return statusFilteredStores.filter((store: Store) => {
      const storeName = (store.name || store.store_name || '').toLowerCase();
      return storeName.includes(searchLower);
    });
  }, [statusFilteredStores, searchLower]);

  const handleConfirm = () => {
    // tempStores에 선택된 매장들만 beforeStores에 저장 (기존 매장은 덮어쓰기)
    const selectedStores = noticeData.tempStores || [];

    // 현재 이행매장 모달에서 나온 매장들의 ID 목록
    const currentModalStoreIds = rawStoreData.map((store: any) => store.store_id);

    // 현재 필터링된 매장들의 ID 목록 (실제로 화면에 보이는 매장들)
    const visibleStoreIds = filteredStores.map((store: any) => store.user_id);

    // 선택된 매장 중에서 현재 필터링되어 보이는 매장들만 필터링
    const actuallySelectedStores = selectedStores.filter((storeId: string) =>
      visibleStoreIds.includes(storeId)
    );

    // 기존 beforeStores에서 현재 모달에 없는 매장들은 유지
    const existingStores = noticeData.beforeStores || [];
    const storesNotInCurrentModal = existingStores.filter((storeId: string) => !currentModalStoreIds.includes(storeId));

    // storesNotInCurrentModal과 actuallySelectedStores를 합쳐 중복을 제거한 최종 매장 목록 생성
    const finalStores = Array.from(new Set([...storesNotInCurrentModal, ...actuallySelectedStores]));

    dispatch(setNoticeRegister({ beforeStores: finalStores }));
    dispatch(setNoticeRegister({ tempStores: [] }));
    props.onApply();
    modalRef.current?.modalClose();
  };

  return (
    <BottomModalV2
      ref={modalRef}
      modalTitle={`${props.selectedNotice?.title || ''} 이행매장`}
      footerBtn="적용"
      onClose={handleClose}
      onConfirm={handleConfirm}
    >
      <Box pd="20px 15px">
        <SearchInput
          type="text"
          id="search-input"
          placeholder="매장명을 검색해주세요."
          defaultValue={searchContent}
          onChange={(e) => setSearchContent(e.target.value)}
          onClick={searchContents}
          onKeyPress={onKeyPress}
          deleteSearchContentFunc={deleteSearchContents}
        />
      </Box>

      <FilterContainer>
        <Button
          className="unfulfilled-filter"
          variant="contained"
          br="round"
          color={showUnfulfilledOnly ? "active" : "secondary"}
          size="medium"
          onClick={() => setShowUnfulfilledOnly(!showUnfulfilledOnly)}
        >
          {showUnfulfilledOnly ? (
            <SVG name="toggleActive" width={16} height={16} viewBox="0 0 16 16" />
          ) : (
            <SVG name="toggle" width={16} height={16} viewBox="0 0 16 16" />
          )}
          미이행만
        </Button>
        <ItemCount>{filteredStores.length}개</ItemCount>
      </FilterContainer>

      <Box pd="0">
        {isLoading ? (
          <LoadingContainer>로딩 중...</LoadingContainer>
        ) : filteredStores.length > 0 ? (
          <FulfillListItem storeList={filteredStores as any} reduxName={"tempStores"} />
        ) : (
          <NoDataContainer>이행매장이 없습니다.</NoDataContainer>
        )}
      </Box>
    </BottomModalV2>
  );
};

const LoadingContainer = styled.div`
  padding: 40px 0;
  text-align: center;
  color: ${({ theme }) => theme.color.tertiary};
`;

const NoDataContainer = styled.div`
  padding: 40px 0;
  text-align: center;
  color: ${({ theme }) => theme.color.tertiary};
`;

const FilterContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 15px 15px;

  .unfulfilled-filter {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 8px 12px;
    font-size: 14px;
    font-weight: 500;
  }
`;

const ItemCount = styled.span`
  font-size: 14px;
  color: ${({ theme }) => theme.color.tertiary};
`;

export default forwardRef(PreviousExecuteStoresModal);